import React, { useState } from 'react'
import { useStationOperationsExport } from '../hooks/useStationOperationsExport'
import { collectStationOperationsRows } from 'BoostXpressAdmin/Utils/stationOperationsExport'
import { StationLookupOption } from 'BoostXpressAdmin/Services/stationLookupBXService'
import StationOperationsStationFilter from '../components/StationOperationsStationFilter'
import filterIcon from 'BoostXpressAdmin/Assets/Icon/Stations/filter.svg'
import exportIcon from 'BoostXpressAdmin/Assets/Icon/Stations/export.svg'
import { stockDeliveryBXService } from 'BoostXpressAdmin/Services/stockDeliveryBXService'
import {
  StockDeliveryItem,
  StockDeliveryProduct,
  StockDeliveryStatus,
} from 'BoostXpressAdmin/Model/stockDeliveryXModel'
import { useStationOperationsPage } from '../hooks/useStationOperationsList'
import ListPagination from 'Components/ListPagination/ListPagination'
import { useListPagination } from 'Hooks/useListPagination'
import {
  formatStationLitres,
  stationOperationsError,
} from 'BoostXpressAdmin/Utils/stationOperations'
import './StockDelivery.scss'

const StockDelivery: React.FC = () => {
  const { exportCsv: downloadCsv, isExporting, exportError } = useStationOperationsExport()
  const [selectedStation, setSelectedStation] = useState<StationLookupOption | null>(null)
  const [statusFilter, setStatusFilter] = useState<StockDeliveryStatus | 'all'>('all')
  const [productFilter, setProductFilter] = useState<StockDeliveryProduct | 'all'>('all')
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const { page, setPage, limit, changePageSize } = useListPagination()

  const filters: Omit<Parameters<typeof stockDeliveryBXService.list>[0], 'page' | 'limit'> = {
    stationId: selectedStation?.id,
    status: statusFilter === 'all' ? undefined : statusFilter === 'Logged' ? 'LOGGED' : 'VERIFIED',
    product: productFilter === 'all' ? undefined : productFilter,
  }

  const {
    items: items,
    total,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useStationOperationsPage<
    StockDeliveryItem,
    {
      stationId?: string
      status?: 'LOGGED' | 'VERIFIED'
      product?: StockDeliveryProduct
    }
  >('stockDelivery', stockDeliveryBXService.list, filters, page, limit)
  const exportCsv = () =>
    downloadCsv(
      () => collectStationOperationsRows('stock_delivery', stockDeliveryBXService.list, filters),
      (item) => ({
        Station: item.station,
        Product: item.product,
        'Depot Invoice Qty': formatStationLitres(item.depotInvoiceQty),
        'Quantity Received': formatStationLitres(item.quantityReceived),
        'Truck Siding': formatStationLitres(item.truckSiding),
        Date: item.date,
        Status: item.status,
      }),
      'stock_delivery',
    )
  const renderStatusBadge = (status: StockDeliveryStatus) => {
    switch (status) {
      case 'Logged':
        return <span className='bx-sd-pill bx-sd-pill--overage'>Logged</span>
      case 'Verified':
        return <span className='bx-sd-pill bx-sd-pill--shortage'>Verified</span>
      default:
        return <span className='bx-sd-pill'>{status}</span>
    }
  }

  return (
    <div className='bx-stock-delivery-page'>
      {exportError && (
        <div className='alert alert-danger' role='alert'>
          {exportError}
        </div>
      )}
      {isError && (
        <div className='alert alert-danger' role='alert'>
          {stationOperationsError(error)}{' '}
          <button type='button' onClick={() => refetch()}>
            Try again
          </button>
        </div>
      )}
      {/* Header section matching Figma & Reconciliation */}
      <div className='bx-stock-delivery-page__header'>
        <h2 className='bx-stock-delivery-page__title'>Stock Delivery</h2>
        <p className='bx-stock-delivery-page__subtitle'>
          Fuel deliveries logged by station managers, kept separate from the daily reporting flow
          since they don&apos;t happen every day.
        </p>
      </div>

      {/* Search & Actions Toolbar matching Reconciliation */}
      <div className='bx-stock-delivery-page__controls'>
        <StationOperationsStationFilter
          value={selectedStation}
          onChange={(station) => {
            setSelectedStation(station)
            setPage(1)
          }}
        />

        <div className='bx-stock-delivery-page__actions'>
          <div className='bx-filter-dropdown-wrapper'>
            <button
              type='button'
              className={`bx-btn-outline ${
                statusFilter !== 'all' || productFilter !== 'all' ? 'active' : ''
              }`}
              onClick={() => setIsFilterOpen(!isFilterOpen)}
            >
              <img src={filterIcon} alt='' />
              <span>Filters</span>
            </button>

            {isFilterOpen && (
              <div className='bx-filter-menu'>
                <div className='bx-filter-menu__title'>Status</div>
                <button
                  type='button'
                  className={`bx-filter-menu__item ${statusFilter === 'all' ? 'selected' : ''}`}
                  onClick={() => {
                    setStatusFilter('all')
                    setPage(1)
                  }}
                >
                  All Statuses
                </button>
                <button
                  type='button'
                  className={`bx-filter-menu__item ${statusFilter === 'Logged' ? 'selected' : ''}`}
                  onClick={() => {
                    setStatusFilter('Logged')
                    setPage(1)
                  }}
                >
                  Logged
                </button>
                <button
                  type='button'
                  className={`bx-filter-menu__item ${statusFilter === 'Verified' ? 'selected' : ''}`}
                  onClick={() => {
                    setStatusFilter('Verified')
                    setPage(1)
                  }}
                >
                  Verified
                </button>

                <div className='bx-filter-menu__divider' />

                <div className='bx-filter-menu__title'>Product</div>
                <button
                  type='button'
                  className={`bx-filter-menu__item ${productFilter === 'all' ? 'selected' : ''}`}
                  onClick={() => {
                    setProductFilter('all')
                    setPage(1)
                  }}
                >
                  All Products
                </button>
                <button
                  type='button'
                  className={`bx-filter-menu__item ${productFilter === 'PMS' ? 'selected' : ''}`}
                  onClick={() => {
                    setProductFilter('PMS')
                    setPage(1)
                  }}
                >
                  PMS
                </button>
                <button
                  type='button'
                  className={`bx-filter-menu__item ${productFilter === 'AGO' ? 'selected' : ''}`}
                  onClick={() => {
                    setProductFilter('AGO')
                    setPage(1)
                  }}
                >
                  AGO
                </button>

                <div className='bx-filter-menu__divider' />
                <button
                  type='button'
                  className='bx-filter-menu__item bx-filter-menu__item--done'
                  onClick={() => setIsFilterOpen(false)}
                >
                  Done
                </button>
              </div>
            )}
          </div>

          <button
            type='button'
            className='bx-btn-outline'
            onClick={exportCsv}
            disabled={isFetching || isExporting || isError || !total}
          >
            <img src={exportIcon} alt='' />
            <span>{isExporting ? 'Exporting...' : 'Export'}</span>
          </button>
        </div>
      </div>

      {/* Main Table matching Figma and Reconciliation */}
      <div className='bx-stock-delivery-page__table-wrapper'>
        <table className='bx-stock-delivery-table'>
          <thead>
            <tr>
              <th>Station</th>
              <th>Product</th>
              <th>Depot invoice qty</th>
              <th>Quantity received</th>
              <th>Truck siding</th>
              <th>Date</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={7} className='bx-sd-empty'>
                  Loading stock deliveries...
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={7} className='bx-sd-empty'>
                  No stock deliveries found.
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id}>
                  <td className='bx-sd-col-station'>{item.station}</td>
                  <td className='bx-sd-col-product'>{item.product}</td>
                  <td className='bx-sd-col-qty'>{formatStationLitres(item.depotInvoiceQty)}</td>
                  <td className='bx-sd-col-qty'>{formatStationLitres(item.quantityReceived)}</td>
                  <td className='bx-sd-col-qty'>{formatStationLitres(item.truckSiding)}</td>
                  <td className='bx-sd-col-date'>{item.date}</td>
                  <td>{renderStatusBadge(item.status)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <ListPagination
        page={page}
        limit={limit}
        total={total}
        disabled={isFetching}
        onPageChange={setPage}
        onPageSizeChange={changePageSize}
      />
    </div>
  )
}

export default StockDelivery
